/**
 * Supabase client  -  singleton instance shared across the app.
 *
 * Reads from Vite-injected environment variables:
 *   - VITE_SUPABASE_URL
 *   - VITE_SUPABASE_PUBLISHABLE_KEY
 *
 * If either is missing, the client is null and the app falls back to
 * pure-localStorage mode. This keeps local development frictionless and
 * lets the app continue working if Supabase is unreachable.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

export const supabase: SupabaseClient | null =
  url && key
    ? createClient(url, key, {
        auth: {
          // Persist session in localStorage so reload doesn't kick the user out.
          persistSession: true,
          // Auto-refresh JWTs in the background.
          autoRefreshToken: true,
          // Detect ?code= in the URL after magic-link redirect and complete sign-in.
          detectSessionInUrl: true,
          // Keep storage isolated under one prefix.
          storageKey: 'btc-stack-planner:auth',
        },
      })
    : null;

/** Convenience for "is cloud sync available right now?" checks throughout the app. */
export const cloudEnabled = (): boolean => supabase !== null;
